import type { Editor } from '@tiptap/react'
import type { LucideIcon } from 'lucide-react'
import {
  Bold,
  CheckSquare,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Highlighter,
  Italic,
  Link,
  List,
  ListOrdered,
  Quote,
  Redo,
  Smile,
  Strikethrough,
  Table,
  Underline,
  Undo,
} from 'lucide-react'

export interface ToolbarButtonDefinition {
  id: string
  icon: LucideIcon
  label: string
  shortcut?: string
  action: (editor: Editor) => void
  isActive?: (editor: Editor) => boolean
  canExecute?: (editor: Editor) => boolean
}

export const undoRedoButtons: ToolbarButtonDefinition[] = [
  {
    id: 'undo',
    icon: Undo,
    label: 'Undo',
    shortcut: 'Mod+Z',
    action: (editor) => editor.chain().focus().undo().run(),
    canExecute: (editor) => editor.can().undo(),
  },
  {
    id: 'redo',
    icon: Redo,
    label: 'Redo',
    shortcut: 'Mod+Shift+Z',
    action: (editor) => editor.chain().focus().redo().run(),
    canExecute: (editor) => editor.can().redo(),
  },
]

export const textFormattingButtons: ToolbarButtonDefinition[] = [
  {
    id: 'bold',
    icon: Bold,
    label: 'Bold',
    shortcut: 'Mod+B',
    action: (editor) => editor.chain().focus().toggleBold().run(),
    isActive: (editor) => editor.isActive('bold'),
  },
  {
    id: 'italic',
    icon: Italic,
    label: 'Italic',
    shortcut: 'Mod+I',
    action: (editor) => editor.chain().focus().toggleItalic().run(),
    isActive: (editor) => editor.isActive('italic'),
  },
  {
    id: 'underline',
    icon: Underline,
    label: 'Underline',
    shortcut: 'Mod+U',
    action: (editor) => editor.chain().focus().toggleUnderline().run(),
    isActive: (editor) => editor.isActive('underline'),
  },
  {
    id: 'strikethrough',
    icon: Strikethrough,
    label: 'Strikethrough',
    shortcut: 'Mod+Shift+S',
    action: (editor) => editor.chain().focus().toggleStrike().run(),
    isActive: (editor) => editor.isActive('strike'),
  },
  {
    id: 'code',
    icon: Code,
    label: 'Code',
    shortcut: 'Mod+E',
    action: (editor) => editor.chain().focus().toggleCode().run(),
    isActive: (editor) => editor.isActive('code'),
  },
]

export const headingButtonsBasic: ToolbarButtonDefinition[] = [
  {
    id: 'heading1',
    icon: Heading1,
    label: 'Heading 1',
    shortcut: 'Mod+Alt+1',
    action: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 1 }),
  },
  {
    id: 'heading2',
    icon: Heading2,
    label: 'Heading 2',
    shortcut: 'Mod+Alt+2',
    action: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 2 }),
  },
]

export const headingButtonsExtended: ToolbarButtonDefinition[] = [
  {
    id: 'heading3',
    icon: Heading3,
    label: 'Heading 3',
    shortcut: 'Mod+Alt+3',
    action: (editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 3 }),
  },
  {
    id: 'heading4',
    icon: Heading4,
    label: 'Heading 4',
    shortcut: 'Mod+Alt+4',
    action: (editor) => editor.chain().focus().toggleHeading({ level: 4 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 4 }),
  },
]

export const listButtonsBasic: ToolbarButtonDefinition[] = [
  {
    id: 'bulletList',
    icon: List,
    label: 'Bullet List',
    shortcut: 'Mod+Shift+8',
    action: (editor) => editor.chain().focus().toggleBulletList().run(),
    isActive: (editor) => editor.isActive('bulletList'),
  },
  {
    id: 'orderedList',
    icon: ListOrdered,
    label: 'Numbered List',
    shortcut: 'Mod+Shift+7',
    action: (editor) => editor.chain().focus().toggleOrderedList().run(),
    isActive: (editor) => editor.isActive('orderedList'),
  },
]

export const taskListButton: ToolbarButtonDefinition = {
  id: 'taskList',
  icon: CheckSquare,
  label: 'Task List',
  shortcut: 'Mod+Shift+9',
  action: (editor) => editor.chain().focus().toggleTaskList().run(),
  isActive: (editor) => editor.isActive('taskList'),
}

export const blockElementButtons: ToolbarButtonDefinition[] = [
  {
    id: 'blockquote',
    icon: Quote,
    label: 'Quote',
    shortcut: 'Mod+Shift+B',
    action: (editor) => editor.chain().focus().toggleBlockquote().run(),
    isActive: (editor) => editor.isActive('blockquote'),
  },
  {
    id: 'table',
    icon: Table,
    label: 'Table',
    action: (editor) =>
      editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
  },
]

export const linkButton: ToolbarButtonDefinition = {
  id: 'link',
  icon: Link,
  label: 'Link',
  shortcut: 'Mod+K',
  action: (editor) => {
    const previousUrl = editor.getAttributes('link').href ?? ''
    const url = window.prompt('Enter URL:', previousUrl)
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  },
  isActive: (editor) => editor.isActive('link'),
}

export const emojiButton: ToolbarButtonDefinition = {
  id: 'emoji',
  icon: Smile,
  label: 'Emoji',
  action: (editor) => editor.chain().focus().insertContent('😀').run(),
}

export const highlightButton: ToolbarButtonDefinition = {
  id: 'highlight',
  icon: Highlighter,
  label: 'Highlight',
  action: (editor) => editor.chain().focus().toggleHighlight().run(),
  isActive: (editor) => editor.isActive('highlight'),
}
